/**
 * PHOTO EDITOR MODAL
 * 
 * Modal for editing photo metadata and fixture linkage
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { PHOTO_CATEGORIES } from '../../constants/photoCategories';
import { getFixturesForAreaType } from '../../constants/fixtureDefinitions';

const PhotoEditor = ({ 
  photo, 
  areas = [], 
  isOpen, 
  onClose, 
  onSave 
}) => {
  const [editedPhoto, setEditedPhoto] = useState(photo || {});
  
  useEffect(() => {
    if (photo) {
      setEditedPhoto(photo);
    }
  }, [photo]);

  if (!isOpen || !photo) return null;

  const handleSave = () => {
    onSave(editedPhoto);
    onClose();
  };

  const handleChange = (field, value) => {
    setEditedPhoto(prev => ({
      ...prev,
      [field]: value,
      // Reset fixture when area changes
      ...(field === 'area_id' ? { fixture_key: '' } : {}),
    }));
  };

  // Get fixtures for selected area
  const selectedArea = areas.find(a => a.area_id === editedPhoto.area_id);
  const availableFixtures = selectedArea 
    ? Object.entries(getFixturesForAreaType(selectedArea.type))
        .filter(([key, fixture]) => {
          // Only show fixtures that are marked for replacement
          const fixtureState = selectedArea.fixtures?.[key];
          return fixtureState?.condition === 'exists_replace' || fixtureState?.condition === 'add_new';
        })
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-slate-900 border border-slate-700 rounded-lg w-full max-w-lg max-h-[90vh] overflow-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-emerald-400">Edit Photo</h3>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Preview */}
        <div className="p-4 border-b border-slate-700">
          <div className="aspect-video bg-slate-800 rounded overflow-hidden">
            {editedPhoto.file_url ? (
              <img 
                src={editedPhoto.file_url} 
                alt="Preview" 
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-6xl text-slate-600">
                📷
              </div>
            )}
          </div>
        </div>
        
        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Category</label>
            <select
              value={editedPhoto.category || 'existing'}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none"
            >
              {PHOTO_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Link to Area */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Link to Area</label>
            <select
              value={editedPhoto.area_id || ''}
              onChange={(e) => handleChange('area_id', e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none"
            >
              <option value="">No area linked</option>
              {areas.map(area => (
                <option key={area.area_id} value={area.area_id}>
                  {area.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Link to Fixture */}
          {selectedArea && availableFixtures.length > 0 && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">Link to Fixture</label>
              <select
                value={editedPhoto.fixture_key || ''}
                onChange={(e) => handleChange('fixture_key', e.target.value)}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none"
              >
                <option value="">No fixture linked</option>
                {availableFixtures.map(([key, fixture]) => (
                  <option key={key} value={key}>
                    {fixture.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Only fixtures marked for replacement or addition are shown
              </p>
            </div>
          )}
          
          {/* Caption */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Caption</label>
            <textarea
              value={editedPhoto.caption || ''}
              onChange={(e) => handleChange('caption', e.target.value)}
              placeholder="Describe what this photo shows..."
              rows={2}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none resize-none"
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoEditor;
